import React, { useCallback, useMemo } from "react";
import { cn } from "@/lib/utils";
import { useFittedFontSize } from "@/hooks/useFittedFontSize";

type AutoFitTextProps = {
  /** Texto usado como referência para o cálculo (normalmente o maior/valor final). */
  textToFit: string;
  minFontPx?: number;
  maxFontPx?: number;
  breathingRoomPx?: number;
  enabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /** Tooltip/atributo title (não precisa ser o mesmo texto exibido). */
  title?: string;
  children?: React.ReactNode;
  as?: "div" | "span";
};

/**
 * Aplica um `font-size` dinâmico para garantir que `textToFit` caiba na largura disponível.
 *
 * SRP: este componente só gerencia “fit” por largura; não anima e não formata valores.
 */
export const AutoFitText: React.FC<AutoFitTextProps> = ({
  textToFit,
  minFontPx,
  maxFontPx,
  breathingRoomPx,
  enabled,
  className,
  style,
  title,
  children,
  as = "div",
}) => {
  const { containerRef, fontSizePx } = useFittedFontSize<
    HTMLDivElement | HTMLSpanElement
  >({
    text: textToFit,
    minFontPx,
    maxFontPx,
    breathingRoomPx,
    enabled,
  });

  const isSpan = as === "span";
  const setContainerRef = useCallback(
    (node: HTMLDivElement | HTMLSpanElement | null) => {
      containerRef.current = node;
    },
    [containerRef],
  );
  const mergedStyle = useMemo<React.CSSProperties>(() => {
    return {
      ...style,
      fontSize: `${fontSizePx}px`,
    };
  }, [fontSizePx, style]);

  const elementProps = {
    ref: setContainerRef,
    className: cn(
      "min-w-0 w-full",
      isSpan ? "inline-block align-baseline" : undefined,
      className,
    ),
    style: mergedStyle,
    title: title ?? textToFit,
    children: children ?? textToFit,
  };

  return isSpan ? <span {...elementProps} /> : <div {...elementProps} />;
};
